"use client";

/**
 * Bottom sheet opened by the "More" slot of the MobileTabBar. Holds
 * destinations and actions that didn't earn a primary tab slot -
 * language picker, install link (when not yet installed), profile,
 * sign in/out, and legal footer links.
 *
 * Not a route - the tab bar manages open/close state and renders
 * this alongside the fixed bar. Closes on backdrop tap, Escape key,
 * or any internal link click.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "@/lib/localized-link";
import { Globe, Smartphone, FileText, Shield, Cookie, LogIn, Clock, Building2, Heart, Megaphone, BarChart3, Newspaper, ChevronDown, Check, Sun, Moon, Monitor, Bell, Map as MapIcon, MessageSquare } from "lucide-react";
import { NAV_GROUPS } from "@/lib/nav-groups";
import { enabledItems, useFeatures } from "@/lib/use-features";
import { useAnnouncements, useMarksNew } from "@/lib/use-announcements";
import NewPill from "./NewPill";
import { useLatestRelease } from "@/lib/use-releases";
import { versionLabel } from "@/lib/announcements";
import { useT } from "@/lib/locale-context";
import { useAuthHref } from "@/lib/use-return-to";
import { useLocale } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { FIRST_PARTY_LISTINGS } from "@/lib/flags";
import { useTheme } from "@/lib/theme-context";
import { useInstallPrompt } from "@/lib/use-install-prompt";
import { useSheetDrag } from "@/lib/use-sheet-drag";
import { useBackToClose } from "@/lib/use-back-to-close";

interface Props {
  open: boolean;
  onClose: () => void;
}

const LANGUAGES: { code: "az" | "ru" | "en"; label: string }[] = [
  { code: "az", label: "Azərbaycanca" },
  { code: "ru", label: "Русский" },
  { code: "en", label: "English" },
];

const THEMES: { code: "system" | "light" | "dark"; labelKey: "theme.system" | "theme.light" | "theme.dark"; Icon: typeof Sun }[] = [
  { code: "system", labelKey: "theme.system", Icon: Monitor },
  { code: "light",  labelKey: "theme.light", Icon: Sun },
  { code: "dark",   labelKey: "theme.dark", Icon: Moon },
];

// Which nav groups the person opened last time (the sheet is an
// accordion since 2026-10-09: six open groups made it a full page).
const OPEN_KEY = "menzil.more.open";

export default function MobileMoreSheet({ open, onClose }: Props) {
  // Portals need a DOM: nothing renders on the server pass.
  const [portalReady, setPortalReady] = useState(false);
  useEffect(() => setPortalReady(true), []);
  // Hardware Back closes this overlay instead of leaving the page.
  useBackToClose(open, onClose);
  const t = useT();
  const { locale, setLocale, prefetchLocales } = useLocale();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const flags = useFeatures();
  const [openSetting, setOpenSetting] = useState<"lang" | "theme" | null>(null);
  const { installed } = useInstallPrompt();
  const signinHref = useAuthHref("login");
  const { newFor, seeHref } = useAnnouncements();
  // The release dot is for everyone the release is news to (lib/use-
  // releases); the "new" pills only for established signed-in people
  // (lib/use-announcements).
  const { latest, dot: releaseUnseen } = useLatestRelease(user);
  const marksNew = useMarksNew();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!open) return;
    try { setOpenGroups(JSON.parse(localStorage.getItem(OPEN_KEY) || "{}")); } catch { /* private mode */ }
  }, [open]);
  const toggleGroup = (key: string) => setOpenGroups((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    return next;
  });

  // The sheet holds the language picker: warm the other locales' strings
  // for this page so a pick swaps the text at once.
  useEffect(() => {
    if (open) prefetchLocales();
  }, [open, prefetchLocales]);

  // Dismiss on Escape so keyboard users aren't stranded on a modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock background scroll while the sheet is open. Without this, a
  // downward drag on the pill (or anywhere in an overscroll area)
  // scrolled the page underneath, making the gesture feel broken.
  // Snapshot + restore the previous overflow so we don't stomp on a
  // page that was already locked (e.g. from another modal).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ── Swipe-down-to-close gesture on the drag handle ─────────────
  // The handle pill visually signals "drag me", so the sheet should
  // actually respond. Shared app-wide gesture - see use-sheet-drag.ts
  // (this sheet is where the behaviour was originally prototyped).
  const { offset: dragOffset, dragging, handleProps, panelRef } = useSheetDrag(onClose, open);

  // Enter / exit animation. Tailwind's `animate-in slide-in-from-
  // bottom` classes rely on tailwindcss-animate which isn't
  // installed here, so those classes were silently no-ops.
  //
  // Two local states drive the animation:
  //   - `render`   - is the component in the DOM at all?
  //   - `entered`  - is the transform class pinning it at 0 (visible)
  //                  or at translateY(100%) (off-screen)?
  //
  // Open:  render → true, then RAF flips entered → true.
  // Close: entered → false, wait for the 200 ms transition to finish,
  //        then render → false. This is what keeps the exit animated
  //        - previously returning null the instant open went false
  //        unmounted us before the transform could animate out.
  const ANIM_MS = 200;
  const [render, setRender] = useState(false);
  const [entered, setEntered] = useState(false);

  // Mount/unmount driven by `open`. Delayed unmount on close so the
  // exit transform has time to play.
  useEffect(() => {
    if (open) {
      setRender(true);
      return;
    }
    setEntered(false);
    const timer = setTimeout(() => setRender(false), ANIM_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // Flip to entered=true in a SEPARATE effect that fires after the
  // render-true commit has been painted. Tying this to the [render]
  // dep (rather than [open]) was the fix for the flaky open
  // animation - when both setRender(true) and the RAF callback
  // landed in the same commit, the browser skipped straight to the
  // final transform. Now the first commit paints with entered=false
  // (translateY 100%), this effect runs on the following tick, RAF
  // queues the state flip for the next animation frame, and the
  // transform transition fires properly.
  useEffect(() => {
    if (!render || !open) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [render, open]);

  // The "what's new" row: first while the newest release is unseen,
  // after the groups once it was read (owner, 2026-10-09: "why do I
  // still see it at the top").
  const whatsNew = (
    <section>
      <Link
        href="/whats-new"
        onClick={onClose}
        className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50"
      >
        <Newspaper className="w-4 h-4 shrink-0 text-gray-500" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block font-medium">{t("wn.nav")}</span>
          {latest && (
            <span className="block truncate text-xs text-gray-500">
              {latest.version ? `${versionLabel(latest.version)}: ` : ""}{latest.title}
            </span>
          )}
        </span>
        {releaseUnseen && (
          <>
            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-gray-400" />
            <span className="sr-only">{t("wn.unseen")}</span>
          </>
        )}
      </Link>
    </section>
  );

  if (!render) return null;

  if (!portalReady) return null;

  return createPortal(
    <>
      {/* Backdrop fades with the sheet - 0 opacity before entered,
          full after. Same RAF-driven gate so they animate together. */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          opacity: entered ? 1 : 0,
          transition: "opacity 200ms ease-out",
        }}
        className="fixed inset-0 z-[1110] bg-black/30 backdrop-blur-[1px]"
      />
      <div ref={panelRef}
        role="dialog"
        aria-modal="true"
        // Stop clicks inside the sheet from bubbling to the backdrop.
        // Belt-and-suspenders: the backdrop is a sibling element so
        // sheet clicks shouldn't reach it anyway, but an earlier
        // broken animation state left the sheet offscreen and clicks
        // went through to the backdrop, dismissing the sheet. This
        // guard ensures any future regression doesn't resurrect that.
        onClick={(e) => e.stopPropagation()}
        // Transform composes two states:
        //   - Enter: starts at translateY(100%), flips to 0 after RAF.
        //   - Drag: translateY(dragOffset px) while user's finger is
        //     down; springs back to 0 on release (unless it crosses
        //     the close threshold, in which case onClose unmounts us).
        // Transition is skipped while actively dragging so the finger
        // tracks 1:1; otherwise 200 ms ease-out handles both enter
        // and spring-back.
        style={{
          transform: !entered
            ? "translateY(100%)"
            : `translateY(${dragOffset}px)`,
          transition: dragging ? "none" : "transform 200ms ease-out",
        }}
        // flex-col with an INNER scroll area: the sheet itself used to
        // scroll, which carried the drag handle and the X off-screen on
        // tall content - they must stay reachable at any scroll depth.
        className="fixed inset-x-0 bottom-0 z-[1120] flex flex-col overflow-hidden rounded-t-3xl bg-white pt-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl max-h-[min(85dvh,calc(100dvh-3rem))] will-change-transform"
      >
        {/* Drag handle - swipe down past 80 px to dismiss. Larger
            hit area than the visual pill so finger drag feels
            forgiving on small phones; py-3 gives about 44 px of
            touchable zone which matches Apple's HIG minimum. */}
        <div
          {...handleProps}
          role="button"
          tabIndex={0}
          aria-label={t("a11y.drag_close")}
          className="flex justify-center py-3 -mt-4 mb-1 cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="h-1.5 w-10 rounded-full bg-gray-300" />
        </div>

        {/* No visible X on a phone sheet (owner, 2026-10-23): a swipe down, a tap outside, Back or Escape close it; screen readers keep the button. */}
        <button type="button" onClick={onClose} className="sr-only">
          Close
        </button>

        <div className="flex-1 overflow-y-auto px-4 sm:px-5 space-y-3 pb-1">
          {releaseUnseen && whatsNew}

          {/* Language and theme on one line: three two-letter pills and
              three icon buttons. The two full sections with labelled
              buttons were a third of the sheet (owner, 2026-10-09). */}
          {/* Language and theme: two settings rows in the sheet's own row
              style, the current value on the right; a tap opens the
              options in place with a tick on the chosen one (owner,
              2026-10-10: pills and segments both read as garbage). */}
          <section className="border-t border-gray-100 pt-1">
            {([
              { id: "lang" as const, Icon: Globe, label: t("nav.language"), value: LANGUAGES.find((l) => l.code === locale)?.label ?? locale,
                options: LANGUAGES.map((l) => ({ key: l.code, label: l.label, Icon: null as typeof Sun | null, selected: locale === l.code, pick: () => setLocale(l.code) })) },
              { id: "theme" as const, Icon: THEMES.find((th) => th.code === theme)?.Icon ?? Monitor, label: t("auth.menu.theme"), value: t(THEMES.find((th) => th.code === theme)?.labelKey ?? "theme.system"),
                options: THEMES.map((th) => ({ key: th.code, label: t(th.labelKey), Icon: th.Icon as typeof Sun | null, selected: theme === th.code, pick: () => setTheme(th.code) })) },
            ]).map((row) => {
              const open = openSetting === row.id;
              return (
                <div key={row.id}>
                  <button type="button" onClick={() => setOpenSetting(open ? null : row.id)} aria-expanded={open} className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm text-gray-900 hover:bg-gray-50">
                    <row.Icon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                    <span className="flex-1">{row.label}</span>
                    <span className="text-gray-500">{row.value}</span>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
                  </button>
                  {open && (
                    <div role="radiogroup" aria-label={row.label} className="pb-1">
                      {row.options.map((o) => (
                        <button key={o.key} type="button" role="radio" aria-checked={o.selected} onClick={() => { o.pick(); setOpenSetting(null); }} className={`flex w-full items-center gap-3 rounded-lg py-2.5 pl-9 pr-2 text-left text-sm hover:bg-gray-50 ${o.selected ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                          {o.Icon && <o.Icon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />}
                          <span className="flex-1">{o.label}</span>
                          {o.selected && <Check className="h-4 w-4 shrink-0 text-gray-900" aria-hidden />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          {/* Your account: the personal pages. */}
          <section className="border-t border-gray-100 pt-3">
            <h3 className="px-2 pb-1 text-xs font-semibold text-gray-400">{t("nav.account")}</h3>
            {user ? (
              <>
                <Link href="/history" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                  <Clock className="w-4 h-4 text-gray-500" />{t("nav.history")}
                </Link>
                <Link href="/watchlist" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                  <Heart className="w-4 h-4 text-gray-500" />{t("watchlist.title")}
                </Link>
                <Link href="/alerts" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                  <Bell className="w-4 h-4 text-gray-500" />{t("nav.alerts")}
                </Link>
                <Link href="/properties" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                  <Building2 className="w-4 h-4 text-gray-500" />{t("myproperties.title")}
                </Link>
                {/* The same list as the desktop account menu; the page
                    itself explains the Pro part (owner, 2026-10-11). */}
                <Link href="/listing-analytics" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                  <BarChart3 className="w-4 h-4 text-gray-500" />{t("la.title")}
                </Link>
                <Link href="/map/personal" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                  <MapIcon className="w-4 h-4 text-gray-500" />{t("map.layer.personal")}
                </Link>
                {FIRST_PARTY_LISTINGS && (
                  <Link href="/profile/listings" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                    <Megaphone className="w-4 h-4 text-gray-500" />{t("mylistings.title")}
                  </Link>
                )}
              </>
            ) : (
              <Link href={signinHref} onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                <LogIn className="w-4 h-4 text-gray-500" />{t("nav.signin")}
              </Link>
            )}
            {/* Reaching us sits in plain sight, not in the Company group
                (owner, 2026-10-13). */}
            <Link href="/contact" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
              <MessageSquare className="w-4 h-4 text-gray-500" />{t("nav.write_us")}
            </Link>
          </section>

          {/* Everything else, in the desktop bar's six groups (one
              definition: lib/nav-groups.ts NAV_GROUPS). */}
          {NAV_GROUPS.map((g) => {
            const isOpen = !!openGroups[g.key];
            const hasNew = marksNew && g.sections.some((sec) => enabledItems(sec.items, flags).some((it) => newFor(it.href).length > 0));
            return (
              <section key={g.key} className="border-t border-gray-100 pt-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(g.key)}
                  aria-expanded={isOpen}
                  className="no-scale flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50"
                >
                  <span className="min-w-0">
                    <span className="block">{t(g.labelKey)}</span>
                    {/* The group's pages named under the title while it is
                        closed, so the features show without opening anything
                        (owner, 2026-10-09: "we don't show them anywhere on
                        mobile"). */}
                    {!isOpen && (
                      <span className="mt-0.5 block truncate text-xs font-normal text-gray-500">
                        {g.sections.flatMap((sec) => enabledItems(sec.items, flags)).slice(0, 6).map((it) => t(it.labelKey)).join(" · ")}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    {!isOpen && hasNew && <NewPill />}
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} aria-hidden />
                  </span>
                </button>
                {/* The open group's pages sit inset under a thin rule, so
                    the title and its pages read as two levels (owner,
                    2026-10-11: "not clear what the hierarchy is"). */}
                {isOpen && (
                  <div className="mb-1 ml-3 border-l-2 border-gray-100 pl-2">
                    {g.sections.map((sec, si) => (
                      <div key={si} className="pb-1">
                        {sec.headingKey && <p className="px-2 pb-0.5 pt-1.5 text-xs font-medium text-gray-500">{t(sec.headingKey)}</p>}
                        <div className={sec.headingKey ? "grid grid-cols-2" : ""}>
                          {enabledItems(sec.items, flags).map((it) => (
                            <Link key={it.href} href={it.href} onClick={() => { seeHref(it.href); onClose(); }} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                              <span className="flex-1">{t(it.labelKey)}</span>
                              {marksNew && newFor(it.href).length > 0 && <NewPill />}
                              {it.pro && <span className="rounded bg-amber-100 px-1 py-0.5 text-xs font-bold text-amber-700">Pro</span>}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {!releaseUnseen && <div className="border-t border-gray-100 pt-1">{whatsNew}</div>}

          {!installed && (
            <section className="border-t border-gray-100 pt-3">
              <Link href="/install" onClick={onClose} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50">
                <Smartphone className="w-4 h-4 text-gray-500" />{t("nav.install")}
              </Link>
            </section>
          )}

          {/* Legal */}
          <section className="border-t border-gray-100 pt-3">
            <div className="grid grid-cols-3 gap-1">
              <Link
                href="/terms"
                onClick={onClose}
                className="flex flex-col items-center gap-1 py-2 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
              >
                <FileText className="w-3.5 h-3.5" />
                {t("nav.terms")}
              </Link>
              <Link
                href="/privacy"
                onClick={onClose}
                className="flex flex-col items-center gap-1 py-2 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
              >
                <Shield className="w-3.5 h-3.5" />
                {t("nav.privacy")}
              </Link>
              <Link
                href="/cookies"
                onClick={onClose}
                className="flex flex-col items-center gap-1 py-2 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
              >
                <Cookie className="w-3.5 h-3.5" />
                {t("nav.cookies")}
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  , document.body);
}
